// Mobile bottom nav — four primary tabs + "More" sheet for overflow.
// Picked up the same primary set the prototype uses (today/chat/food/todo);
// the remaining sections live in the sheet which slides up on tap.

import { useEffect, useState } from "preact/hooks";
import { Icon } from "../icons";
import type { Tab } from "./topbar";

const PRIMARY = new Set(["today", "chat", "food", "todo"]);

interface MobileNavProps {
  tabs: Tab[];
  tab: string;
  setTab: (id: string) => void;
}

export function MobileNav({ tabs, tab, setTab }: MobileNavProps) {
  const [moreOpen, setMoreOpen] = useState(false);

  // Body-level signal so unrelated overlays (the QuickChat FAB, etc.)
  // can react. Used by CSS in quick-chat.css to hide the vulture FAB
  // while the More sheet is open — otherwise the FAB sits on top of
  // the Studio tile and blocks tap.
  useEffect(() => {
    if (moreOpen) document.body.dataset.moreOpen = "1";
    else delete document.body.dataset.moreOpen;
    return () => { delete document.body.dataset.moreOpen; };
  }, [moreOpen]);
  // When the parent passes a small set of tabs (kiosk profile e.g.
  // ?kiosk=kitchen → 3 tabs), skip the PRIMARY filter entirely so we
  // don't push half the kiosk into an empty More sheet. 4 fits cleanly
  // alongside the More button on a phone; 5+ uses the overflow as
  // designed. PRIMARY-set membership only applies above the threshold.
  const primary = tabs.length <= 4
    ? tabs
    : tabs.filter((t) => PRIMARY.has(t.id));
  const overflow = tabs.length <= 4
    ? []
    : tabs.filter((t) => !PRIMARY.has(t.id));
  const isOverflowActive = overflow.some((t) => t.id === tab);

  return (
    <>
      <nav className="mob-nav">
        {primary.map((t) => (
          <button
            key={t.id}
            className="mob-tab"
            data-active={tab === t.id}
            onClick={() => { setTab(t.id); setMoreOpen(false); }}
          >
            <Icon name={t.icon} size={20} />
            <span>{t.label}</span>
          </button>
        ))}
        {overflow.length > 0 && (
          <button
            className="mob-tab"
            data-active={moreOpen || isOverflowActive}
            onClick={() => setMoreOpen((o) => !o)}
          >
            <Icon name={moreOpen ? "x" : "more"} size={20} />
            <span>More</span>
          </button>
        )}
      </nav>

      {moreOpen && (
        <div className="mob-sheet-bg" onClick={() => setMoreOpen(false)}>
          <div className="mob-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="mob-sheet-handle" />
            <div className="mob-sheet-grid">
              {overflow.map((t) => (
                <button
                  key={t.id}
                  className="mob-sheet-tile"
                  data-active={tab === t.id}
                  onClick={() => { setTab(t.id); setMoreOpen(false); }}
                >
                  <span className="mob-sheet-icon"><Icon name={t.icon} size={20} /></span>
                  <span style={{ fontWeight: 500 }}>{t.label}</span>
                  <Icon name="arrow-right" size={14} />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
